import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from '@tencent/dui/lib/components/Modal';
import Radio from '@tencent/dui/lib/components/Radio';
import Button from '@tencent/dui/lib/components/Button';
import Menu from '@tencent/dui/lib/components/Menu';
import i18n from '@i18next/i18n';
import Styles from './styles.css';
import { NumberInput } from '@tencent/wb-ext-form';
import TabstopsToBeCleared from './components/tabstops-to-be-cleared';
import { ptToCm, cmToPt } from '@src/slide/infrastructure/module/independent/unit';
import type { ITabstop } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import Unit from './components/unit';
import { MAX_CM, MIN_CM, MIN_TABSTOP_CM } from './const';

export interface ITabstopModalProps {
    isVisible: boolean;
    setVisible: (visible: boolean) => void;
    tabList?: ITabstop[];
    tabSize?: number;
    setTabSize: (tabSize: number) => void;
    setTabList: (tabList: ITabstop[]) => void;
}

type TabstopType = 'left' | 'right' | 'center' | 'decimal' | 'bar';

const CmUnit = () => <Unit unitStr={i18n.t('厘米')} />;

const SmallGap = () => <div style={{ marginBottom: '10px' }}></div>;

const TabstopModal: React.FC<ITabstopModalProps> = (props) => {
    const { isVisible, setVisible } = props;
    const [selectedTabstopType, setSelectedTabstopType] = useState<TabstopType>('left');
    const [selectedTabstop, setSelectedTabstop] = useState<ITabstop | null>(null);
    const [currentTabList, setCurrentTabList] = useState<ITabstop[]>([]);
    const [tabstopPosition, setTabstopPosition] = useState<number>(0);
    const [currentTabSize, setCurrentTabSize] = useState<number | undefined>(undefined);
    const [removingTabList, setRemovingTabList] = useState<number[]>([]);
    const [isRemovingAll, setIsRemovingAll] = useState<boolean>(false);

    const initCurrentTabList = () => {
        if (props.tabList) {
            setCurrentTabList(props.tabList.map(tabstop => ({ ...tabstop, position: ptToCm(tabstop.position, 2) })));
        }
    };

    const initCurrentTabSize = () => {
        if (props.tabSize) {
            setCurrentTabSize(ptToCm(props.tabSize, 2));
        }
    };

    useEffect(() => {
        initCurrentTabList();
    }, [props.tabList]);

    useEffect(() => {
        initCurrentTabSize();
    }, [props.tabSize]);

    const resetAndCloseModal = () => {
        setVisible(false);
        // 为了更好的体验，初始化状态需要有一个短暂的延迟，因为 dui modal 关闭有动画
        const MODAL_CLOSE_TIMEOUT = 200;
        setTimeout(() => {
            initCurrentTabList();
            initCurrentTabSize();
            setRemovingTabList([]);
            setTabstopPosition(0);
            setSelectedTabstopType('left');
            setSelectedTabstop(null);
            setIsRemovingAll(false);
        }, MODAL_CLOSE_TIMEOUT);
    };

    const onModalOk = (event) => {
        // 根据 dui 文档，没有 event 参数表示是 enter 按键触发。
        // 按键触发保存时由于不会触发 input 的 blur，也就无法触发 input 值的合法化校验，导致非法参数被保存，所以在这里禁用 enter 保存
        if (!event) {
            return;
        }
        setVisible(false);
        props.setTabSize(cmToPt(currentTabSize, 2));
        props.setTabList(currentTabList.map(tabstop => ({
            ...tabstop,
            position: cmToPt(tabstop.position, 2),
        })));
    };

    const addTabstopPositionToCurrentTabList = () => {
        const newCurrentTabList = [...currentTabList];
        const existTabstop = newCurrentTabList.find(tabstop => tabstop.position === tabstopPosition);
        if (existTabstop) {
            if (existTabstop.alignment !== selectedTabstopType) {
                existTabstop.alignment = selectedTabstopType;
            }
        } else {
            newCurrentTabList.push({ position: tabstopPosition, alignment: selectedTabstopType });
            newCurrentTabList.sort((a, b) => a.position - b.position);
        }
        setCurrentTabList(newCurrentTabList);
        setSelectedTabstop({
            position: tabstopPosition,
            alignment: selectedTabstopType,
        });
    };

    const removeAllTabstops = () => {
        setIsRemovingAll(true);
        setCurrentTabList([]);
    };

    const onRemoveSelectedTabstop = () => {
        if (selectedTabstop === null) {
            return;
        }
        const newCurrentTabList = [...currentTabList].filter(tabstop => tabstop.position !== selectedTabstop.position);
        setCurrentTabList(newCurrentTabList);
        const isAlreadyRemoving = removingTabList
            .find(removingPosition => removingPosition === selectedTabstop.position);
        if (isAlreadyRemoving) {
            return;
        }
        const newRemovingTabList = [...removingTabList];
        newRemovingTabList.push(selectedTabstop.position);
        setRemovingTabList(newRemovingTabList);
        setSelectedTabstop(null);
    };

    return (
        <Modal
            visible={isVisible}
            title={i18n.t('制表位')}
            onOk={onModalOk}
            onCancel={resetAndCloseModal}
            style={{
                width: '500px',
            }}
        >
            <div className={Styles['tabstop-modal-container']}>
                <div className={Styles['tabstop-modal-left-column']}>
                    <div className={Styles['tabstop-modal-row']}>
                        <span>
                            {`${i18n.t('制表位位置')}${i18n.t('：')}`}
                        </span>
                        <NumberInput
                            step={0.1}
                            min={MIN_CM}
                            max={MAX_CM}
                            allowDecimal
                            toFixed={2}
                            style={{ width: 70 }}
                            value={String(tabstopPosition)}
                            onChange={(changedTabstopPosition: string) => {
                                const floatTabstopPosition = parseFloat(changedTabstopPosition);
                                if (!isNaN(floatTabstopPosition)) {
                                    setTabstopPosition(floatTabstopPosition);
                                }
                            }}
                        >
                        </NumberInput>
                        <CmUnit />
                    </div>
                    <SmallGap />
                    <div className={Styles['tabstop-modal-row']}>
                        <span>
                            {`${i18n.t('默认制表位')}${i18n.t('：')}`}
                        </span>
                        <NumberInput
                            step={0.1}
                            min={MIN_TABSTOP_CM}
                            max={MAX_CM}
                            allowDecimal
                            toFixed={2}
                            style={{ width: 70 }}
                            value={currentTabSize ? String(currentTabSize) : undefined}
                            onChange={(changedTabSize: string) => {
                                const floatChangedTabSize = parseFloat(changedTabSize);
                                if (!isNaN(floatChangedTabSize)) {
                                    setCurrentTabSize(floatChangedTabSize);
                                }
                            }}
                        >
                        </NumberInput>
                        <CmUnit />
                    </div>
                    <SmallGap />
                    <div>
                        <div style={{ marginBottom: '5px' }}>
                            {`${i18n.t('对齐')}${i18n.t('：')}`}
                        </div>
                        <Radio.Group
                            onChange={value => setSelectedTabstopType(value)}
                            value={selectedTabstopType}
                            itemStyle={{ paddingBottom: '10px' }}
                            style={{ marginLeft: '10px ' }}
                        >
                            <Radio value={'left'}>{i18n.t('向左对齐')}</Radio>
                            <Radio value={'center'}>{i18n.t('居中对齐')}</Radio>
                            <Radio value={'right'}>{i18n.t('向右对齐')}</Radio>
                        </Radio.Group>
                    </div>
                    <SmallGap />
                    <div>
                        {`${i18n.t('要清除的制表位')}${i18n.t('：')}`}
                    </div>
                    <div>
                        <TabstopsToBeCleared tabstops={removingTabList} isRemovingAll={isRemovingAll} />
                    </div>
                </div>
                <div className={Styles['tabstop-modal-right-column']}>
                    <div className={Styles['tabstop-modal-tabstop-list']}>
                        <Menu
                            style={{
                                border: 'none',
                                padding: 0,
                            }}
                            onClick={(tabstopPosition: number) => {
                                const tabstop = currentTabList.find(tabstop => tabstop.position === tabstopPosition);
                                if (tabstop) {
                                    setSelectedTabstop({ ...tabstop });
                                    setTabstopPosition(tabstop.position);
                                    // TODO: 待排版层支持小数点对齐制表符后移除该逻辑
                                    const tabstopType = tabstop.alignment === 'decimal' ? 'left' : tabstop.alignment;
                                    setSelectedTabstopType(tabstopType);
                                }
                            }}
                        >
                            {currentTabList.map(tabstop => (
                                <Menu.Item
                                    id={tabstop.position}
                                    key={tabstop.position}
                                    selected={selectedTabstop?.position === tabstop.position}
                                >
                                    {/* regex 用来移除无用小数点和 0 */}
                                    {tabstop.position.toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, '$1') + i18n.t('厘米')}
                                </Menu.Item>
                            ))}
                        </Menu>
                    </div>
                    <SmallGap />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={addTabstopPositionToCurrentTabList}>{i18n.t('设置')}</Button>
                        <Button onClick={onRemoveSelectedTabstop}>{i18n.t('清除')}</Button>
                    </div>
                    <SmallGap />
                    <div style={{ display: 'flex', justifyContent: 'right' }}>
                        <Button
                            onClick={removeAllTabstops}
                            style={{ width: '88px' }}
                        >{i18n.t('全部清除')}</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

TabstopModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    setVisible: PropTypes.func.isRequired,
    tabList: PropTypes.array,
    tabSize: PropTypes.number,
    setTabList: PropTypes.func.isRequired,
    setTabSize: PropTypes.func.isRequired,
};

export default TabstopModal;
